import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Spinner } from 'react-bootstrap';
import Header from '../../components/Header';
import SideBar from '../../components/SideBar';
import { extractJSON } from '../../services/jsonUtil';
import { removeItem } from '../../utils/cache';
import {
  analyticsReset,
  identifyUser,
  SEGMENT_EVENTS,
  trackEvent,
} from '../../utils/Segment';
import { getDisplayType, getUserAttribute } from '../../utils/helpers';
import useWindowWidth from '../../hooks/useWindowWidth';
import {
  IMAGE_CONSTANT,
  LOCAL_STORAGE_CONSTANT,
  TEXT_CONSTANT,
} from '../../constants/app';
import { APP_ROUTES, getRoute } from '../../route';
import {
  doGetUserDataRequest,
  doUpdateUserProfieRequest,
} from '../../store/action/user';
import {
  checkForChange,
  resetStore,
  setSelectedFilters,
  setValuesAreChanged,
} from '../../store/action/common';
import { doPlansSuccess } from '../../store/action/plan';
import PersonalDetails from './PersonalDetails';
import ChangePassword from './ChangePassword';
import ChangeEmail from './ChangeEmail';
import ChangePhone from './ChangePhone';
import Notifications from './Notifications';
import AboutJen from './AboutJen';
import HelpAndSupport from './HelpAndSupport';
import ModalBox from '../../ui-core/Modal';
import LazyImage from '../../ui-core/Image';
import PhotoUploadModel from './PhotoUploadModel';
import { useWebsiteSettings } from '../../contexts/WebsiteSettingsContext';
import PainPoints from '../PainPoints';

import './style.css';
import Payments from './Payments';
import MyOrders from './MyOrders';
import { axiosInstance, resetAxiosInstance } from '../../api';
import {
  extractApiError,
  getCommunityProfileRequest,
  uploadCommunityAvatarRequest,
} from '../../api/community';
import { fetchCommunityProfileRequest } from '../../store/action/community';
import {
  createNotification,
  NOTIFICATION_TYPE,
  SUCCESS_MESSAGES,
} from '../../services/Notification';
import Cookies from 'js-cookie';

const dataUrlToFile = (dataUrl, filename) => {
  const [header, data] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)[1];
  const bytes = atob(data);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new File([arr], filename, { type: mime });
};

const DEFAULT_TERMS_OF_SERVICE_URL =
  'https://docs.google.com/document/d/1d2nS643SoO95afqaKZtL7EHxClNoACPsiFeqevS7PGQ/edit';

const Profile = ({ history, match }) => {
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.user);
  const communityProfile = useSelector(state => state.community?.profile);
  const { websiteSettings } = useWebsiteSettings();
  const displayType = getDisplayType(useWindowWidth());
  const isDesktopScreen = displayType === 'desktop';
  const section = match.params.section;
  const onboardingCompleted = getUserAttribute('onboarding_completed');
  const userName = getUserAttribute('name');
  const [show, setShow] = useState(false);
  const [showOnboardingPrompt, setShowOnboardingPrompt] = useState(false);
  const [showPhotoUploadModal, setShowPhotoUploadModal] = useState(false);
  const [showPhoneChanged, setShowPhoneChanged] = useState(false);
  const [isPhotoUploading, setIsPhotoUploading] = useState(false);
  const nextKeyRef = useRef(null);
  const profilePic = getUserAttribute('profile_pic');
  const displayName = user?.attributes?.name || userName || '';
  const displayProfilePic = user?.attributes?.profile_pic || profilePic;
  const communityAvatar =
    communityProfile?.avatar_url || IMAGE_CONSTANT.defaultProfileIcon;

  // Get URLs from API or use defaults
  const termsUrl = websiteSettings?.terms_url || DEFAULT_TERMS_OF_SERVICE_URL;
  const privacyUrl = websiteSettings?.privacy_url || APP_ROUTES.PRIVACY_POLICY;

  const openTermsOfService = () => {
    window.open(termsUrl, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    const user = extractJSON(LOCAL_STORAGE_CONSTANT.USER);
    if (user?.id) {
      const userId = user?.id;
      dispatch(doGetUserDataRequest({ userId }));
    }
  }, []);

  useEffect(() => {
    if (!communityProfile) {
      dispatch(fetchCommunityProfileRequest());
    }
  }, [dispatch, communityProfile]);

  useEffect(() => {
    if (history.location.state?.phoneChanged) {
      history.replace(history.location.pathname, {});
      setShowPhoneChanged(true);
      const timer = setTimeout(() => setShowPhoneChanged(false), 2500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [section]); // eslint-disable-line

  useEffect(() => {
    if (section === 'privacy-policy') {
      // Check if privacy URL is external or internal route
      if (privacyUrl.startsWith('http')) {
        window.open(privacyUrl, '_blank', 'noopener,noreferrer');
        history.replace(APP_ROUTES.PROFILE);
      } else {
        history.replace(privacyUrl);
      }
    } else if (section === 'terms-conditions') {
      openTermsOfService();
      history.replace(APP_ROUTES.PROFILE);
    }
  }, [section, history, privacyUrl]);

  const handleSectionChange = (section = null) => {
    if (section) {
      const options = {
        pathParam: {
          section,
        },
      };
      const url = getRoute(APP_ROUTES.PROFILE_WITH_SUB_ROUTES, options);
      history?.replace(url);
    } else {
      history?.replace(APP_ROUTES.PROFILE);
    }
  };

  const handleSubmit = (data, callback) => {
    const userId = user?.id;
    identifyUser(userId, {
      country_code: data?.country_code,
      phone: data?.mobile,
      email: data?.email,
      name: data?.name,
      gender: data?.gender,
    });
    trackEvent(SEGMENT_EVENTS.PROFILE_UPDATED, {
      user_id: user?.id,
      user_email: data?.email,
      user_gender: data?.gender,
      user_name: data?.name,
      user_phone: data?.mobile,
      free_trial_active: user?.attributes?.free_trial || false,
      subscription_active:
        user?.attributes?.subscription_details?.subscribed || false,
    });
    dispatch(
      doUpdateUserProfieRequest({
        data,
        callback: () => {
          dispatch(setValuesAreChanged(false));
          callback && callback();
        },
      }),
    );
  };

  const handlePhotoUpload = async ({ filename, content }) => {
    setIsPhotoUploading(true);
    try {
      const file = dataUrlToFile(content, filename);
      await uploadCommunityAvatarRequest(file);
      dispatch(fetchCommunityProfileRequest());
      setShowPhotoUploadModal(false);
      createNotification({
        type: NOTIFICATION_TYPE.SUCCESS,
        message: SUCCESS_MESSAGES.USER_PROFILE_UPDATE_SUCCESS,
      });
    } catch (err) {
      createNotification({
        type: NOTIFICATION_TYPE.ERROR,
        message: extractApiError(err, 'Failed to update profile picture.'),
      });
    } finally {
      setIsPhotoUploading(false);
    }
  };

  const navigateToMemberProfile = async () => {
    let uid = localStorage.getItem(LOCAL_STORAGE_CONSTANT.COMMUNITY_UID);
    if (!uid) {
      try {
        const response = await getCommunityProfileRequest();
        uid = response?.data?.data?.uid ?? null;
        if (uid) {
          localStorage.setItem(LOCAL_STORAGE_CONSTANT.COMMUNITY_UID, uid);
        }
      } catch {
        // member profile unavailable without community uid
      }
    }
    if (uid) {
      history.push(
        getRoute(APP_ROUTES.MEMBER_PROFILE, {
          pathParam: { userId: uid },
        }),
      );
    }
  };

  useEffect(() => {
    if (section === 'my-profile') {
      navigateToMemberProfile();
    }
  }, [section]);

  const proceedToSection = key => {
    if (key === 'my-profile') {
      navigateToMemberProfile();
      return;
    }
    handleSectionChange(key);
  };

  function callback(value, key = null) {
    if (value) {
      setShow(true);
      nextKeyRef.current = key;
    } else {
      proceedToSection(key);
    }
  }

  const handleClick = key => {
    switch (key) {
      case 'my-profile':
        dispatch(
          checkForChange({
            callback: value => callback(value, key),
          }),
        );
        break;
      case 'logout':
        logOut();
        break;
      case 'privacy-policy':
        // Check if privacy URL is external or internal route
        if (privacyUrl.startsWith('http')) {
          window.open(privacyUrl, '_blank', 'noopener,noreferrer');
        } else {
          history.push(privacyUrl);
        }
        break;
      case 'terms-conditions':
        openTermsOfService();
        break;
      case 'goal-assessment':
        history.push(APP_ROUTES.GOAL_ASSESSMENT);
        break;
      default:
        // if user already filled onboarding then allow to navigate to other sections
        if (onboardingCompleted) {
          dispatch(
            checkForChange({
              callback: value => callback(value, key),
            }),
          );
        } else {
          // if user not filled onboarding then show prompt
          setShowOnboardingPrompt(true);
          nextKeyRef.current = key;
        }
        break;
    }
  };
  const logOut = () => {
    trackEvent(SEGMENT_EVENTS.LOGOUT, {
      user_id: user?.id,
      free_trial_active: user?.attributes?.free_trial || false,
      subscription_active:
        user?.attributes?.subscription_details?.subscribed || false,
    });
    dispatch(resetStore());
    dispatch(setSelectedFilters({ selectedFilters: [], skipSave: true }));
    dispatch(doPlansSuccess([]));
    removeItem(LOCAL_STORAGE_CONSTANT.USER);
    removeItem(LOCAL_STORAGE_CONSTANT.SESSION);
    removeItem(LOCAL_STORAGE_CONSTANT.SELECTED_FILTERS);
    removeItem(LOCAL_STORAGE_CONSTANT.LAST_VOLUME);
    removeItem(LOCAL_STORAGE_CONSTANT.LAST_PLAYBACK_RATE);
    removeItem(LOCAL_STORAGE_CONSTANT.BODY_PARTS_OPTIONS);
    removeItem('completeOnboarding');
    removeItem('isAppleLogin');
    removeItem('curatePlanDetails');
    removeItem('isFromLogin');
    removeItem('isAlreadyHavePhoneVerified');

    localStorage.clear();
    sessionStorage.clear();
    Object.keys(Cookies.get()).forEach(cookieName => {
      Cookies.remove(cookieName);
    });

    // Clear axios instance headers
    resetAxiosInstance();
    delete axiosInstance.defaults.headers.common.uid;
    delete axiosInstance.defaults.headers.common.client;
    delete axiosInstance.defaults.headers.common['access-token'];

    history.push(APP_ROUTES.ROOT);
    analyticsReset();
    window?.Cohere?.stop();
    if (window.Intercom) {
      window.Intercom('shutdown');
    }
    window.location.reload();
  };

  const handleBundleDealsRedirection = () => {
    history.push(APP_ROUTES.BUNDLE_DEALS);
  };

  const renderContent = () => {
    switch (section) {
      case 'change-password':
        return <ChangePassword history={history} />;
      case 'change-email':
        return user ? (
          <ChangeEmail history={history} user={user} />
        ) : (
          <div className="d-flex w-100 h-100 justify-content-center align-items-center">
            Retrieving details
            <Spinner className="mx-2" animation="border" />
          </div>
        );
      case 'change-phone':
        return user ? (
          <ChangePhone history={history} user={user} />
        ) : (
          <div className="d-flex w-100 h-100 justify-content-center align-items-center">
            Retrieving details
            <Spinner className="mx-2" animation="border" />
          </div>
        );
      case 'notifications':
        return <Notifications />;
      case 'help-support':
        return <HelpAndSupport />;
      case 'payments':
        return <Payments />;
      case 'my-orders':
        return (
          <MyOrders
            handleBundleDealsRedirection={handleBundleDealsRedirection}
          />
        );
      case 'about-jen':
        return <AboutJen />;
      case 'pain-points':
        return <PainPoints history={history} />;
      case 'edit-profile':
      default:
        return (
          <>
            {user ? (
              <PersonalDetails user={user} />
            ) : (
              <div className="d-flex w-100 h-100 justify-content-center align-items-center">
                Retrieving details
                <Spinner className="mx-2" animation="border" />
              </div>
            )}
          </>
        );
    }
  };

  const renderProfileHeader = () => (
    <div className="profile-page-header">
      <div className="container">
        <div className="profile-page-header-inner">
          <div className="profile-page-header-avatar position-relative">
            <button
              className="avatar-header-edit-btn"
              onClick={() => setShowPhotoUploadModal(true)}
              title="Edit Profile Picture"
            >
              <img
                src={IMAGE_CONSTANT.editIcon1}
                className="edit-icon"
                alt="edit"
              />
            </button>
            <div className="avatar-img-container">
              <LazyImage
                alt="profile"
                className="img-fluid"
                errorSrc={IMAGE_CONSTANT.defaultProfileIcon}
                placeholderSrc={IMAGE_CONSTANT.personIcon}
                src={communityAvatar}
              />
            </div>
          </div>
          <h1 className="profile-page-header-name">{displayName}</h1>
        </div>
      </div>
    </div>
  );

  const defaultBlock = () => (
    <div className="bg-white">
      {renderProfileHeader()}
      <div className="container">
        <div className="profile-page-container d-flex flex-wrap">
          <div className="profile-left">
            <SideBar
              handleClick={handleClick}
              handleUpload={handleSubmit}
              profilePicture={displayProfilePic}
              selectedOption={section || 'edit-profile'}
              userName={displayName}
            />
          </div>
          <div className="profile-right">{renderContent()}</div>
        </div>
      </div>
    </div>
  );

  const renderHeaderTitle = () => {
    switch (section) {
      case 'change-password':
        return 'Change password';
      case 'change-email':
        return 'Change email';
      case 'change-phone':
        return user?.attributes?.mobile
          ? 'Change phone number'
          : 'Add phone number';
      case 'help-support':
        return 'Support';
      case 'payments':
        return 'Payments';
      case 'edit-profile':
        return 'Account Settings';
      case 'my-orders':
        return 'My Orders';
      case 'notifications':
        return 'Notifications';
      case 'pain-points':
        return 'Pain Points';
      case 'about-jen':
        return 'About Jen';
      default:
        break;
    }
  };

  const goBackMobile = () => {
    dispatch(
      checkForChange({
        callback,
      }),
    );
  };

  const discardChanges = discard => {
    if (discard) {
      proceedToSection(nextKeyRef.current);
      dispatch(setValuesAreChanged(false));
    }
    setShow(false);
  };

  const handleOnboardingPrompt = completeNow => {
    setShowOnboardingPrompt(false);
    if (completeNow) {
      history.push(APP_ROUTES.SIGNUP_ONBOARDING);
    }
  };

  return (
    <div className={`profile-page${section ? ' profile-page--section' : ''}`}>
      <Header
        homeClass="home"
        challengeClass="challenge"
        foldersClass="folders"
        hideBackButton={true}
        discoverClass="discover"
        showBackHeader={true}
        mobileHeaderText="Profile"
        iPadHeaderText="Profile"
      />
      {!isDesktopScreen && section && (
        <>
          <div className="profile-section-titlebar">
            <button
              type="button"
              className="profile-section-titlebar-back"
              onClick={goBackMobile}
              aria-label="Back to account settings"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M15.5302 18.9698C15.5999 19.0395 15.6552 19.1222 15.6929 19.2132C15.7306 19.3043 15.75 19.4019 15.75 19.5004C15.75 19.599 15.7306 19.6965 15.6929 19.7876C15.6552 19.8786 15.5999 19.9614 15.5302 20.031C15.4606 20.1007 15.3778 20.156 15.2868 20.1937C15.1957 20.2314 15.0982 20.2508 14.9996 20.2508C14.9011 20.2508 14.8035 20.2314 14.7124 20.1937C14.6214 20.156 14.5387 20.1007 14.469 20.031L6.96899 12.531C6.89926 12.4614 6.84394 12.3787 6.80619 12.2876C6.76845 12.1966 6.74902 12.099 6.74902 12.0004C6.74902 11.9019 6.76845 11.8043 6.80619 11.7132C6.84394 11.6222 6.89926 11.5394 6.96899 11.4698L14.469 3.96979C14.6097 3.82906 14.8006 3.75 14.9996 3.75C15.1986 3.75 15.3895 3.82906 15.5302 3.96979C15.671 4.11052 15.75 4.30139 15.75 4.50042C15.75 4.69944 15.671 4.89031 15.5302 5.03104L8.55993 12.0004L15.5302 18.9698Z"
                  fill="#14142B"
                />
              </svg>
            </button>
            <span className="profile-section-titlebar-text">
              {renderHeaderTitle()}
            </span>
          </div>
        </>
      )}
      {/* Content rendered once — no remount on resize */}
      {section === 'change-password' ? (
        <ChangePassword history={history} />
      ) : section === 'change-email' ? (
        user ? (
          <ChangeEmail history={history} user={user} />
        ) : (
          <div className="d-flex w-100 h-100 justify-content-center align-items-center">
            Retrieving details
            <Spinner className="mx-2" animation="border" />
          </div>
        )
      ) : section === 'change-phone' ? (
        user ? (
          <ChangePhone history={history} user={user} />
        ) : (
          <div className="d-flex w-100 h-100 justify-content-center align-items-center">
            Retrieving details
            <Spinner className="mx-2" animation="border" />
          </div>
        )
      ) : section === 'notifications' ? (
        <Notifications />
      ) : section === 'help-support' && !isDesktopScreen ? (
        <HelpAndSupport />
      ) : section === 'payments' ? (
        <Payments />
      ) : section === 'my-orders' ? (
        <MyOrders handleBundleDealsRedirection={handleBundleDealsRedirection} />
      ) : section === 'about-jen' && !isDesktopScreen ? (
        <AboutJen />
      ) : section === 'pain-points' ? (
        <PainPoints history={history} />
      ) : section === 'edit-profile' && !isDesktopScreen ? (
        user ? (
          <PersonalDetails user={user} />
        ) : (
          <div className="d-flex w-100 h-100 justify-content-center align-items-center">
            Retrieving details
            <Spinner className="mx-2" animation="border" />
          </div>
        )
      ) : (
        defaultBlock()
      )}
      <ModalBox backdrop={'static'} show={show}>
        <div className="discard-changes mt-2">
          <h5 className="text-center">{TEXT_CONSTANT.DISCARD_CHANGES}</h5>
          <div className="d-flex w-100 justify-content-center">
            <Button
              onClick={() => discardChanges(true)}
              variant="danger"
              className="mx-2 cursor-pointer"
            >
              {TEXT_CONSTANT.YES}
            </Button>
            <Button
              onClick={() => discardChanges()}
              variant="dark"
              className="mx-2 cursor-pointer"
            >
              {TEXT_CONSTANT.NO}
            </Button>
          </div>
        </div>
      </ModalBox>
      <ModalBox backdrop={'static'} show={showOnboardingPrompt}>
        <div className="mt-2">
          <h5 className="text-center">
            {TEXT_CONSTANT.ONBOARDING_INCOMPLETE_TITLE}
          </h5>
          <p className="text-center mb-4">
            {TEXT_CONSTANT.ONBOARDING_INCOMPLETE_MESSAGE}
          </p>
          <div className="d-flex justify-content-center align-items-center gap-1">
            <button
              onClick={() => handleOnboardingPrompt(true)}
              variant="success"
              className="w-50 start-free-trail-btn cursor-pointer"
            >
              {TEXT_CONSTANT.COMPLETE_ONBOARDING}
            </button>
            <button
              onClick={() => {
                handleOnboardingPrompt(false);
                handleSectionChange(nextKeyRef.current);
              }}
              variant="secondary"
              className="w-50 start-free-trail-btn cursor-pointer filter-btn bg-white"
            >
              {TEXT_CONSTANT.SKIP_ONBOARDING}
            </button>
          </div>
        </div>
      </ModalBox>
      <PhotoUploadModel
        show={showPhotoUploadModal}
        handleClose={() => setShowPhotoUploadModal(false)}
        isLoading={isPhotoUploading}
        callback={handlePhotoUpload}
      />
      {showPhoneChanged && (
        <div className="change-phone-success-overlay">
          <div className="change-phone-success-card">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
            >
              <path
                d="M16 3C13.4288 3 10.9154 3.76244 8.77759 5.1909C6.63975 6.61935 4.97351 8.64968 3.98957 11.0251C3.00563 13.4006 2.74819 16.0144 3.2498 18.5362C3.75141 21.0579 4.98953 23.3743 6.80762 25.1924C8.6257 27.0105 10.9421 28.2486 13.4638 28.7502C15.9856 29.2518 18.5995 28.9944 20.9749 28.0104C23.3503 27.0265 25.3807 25.3603 26.8091 23.2224C28.2376 21.0846 29 18.5712 29 16C28.9964 12.5533 27.6256 9.24882 25.1884 6.81163C22.7512 4.37445 19.4467 3.00364 16 3ZM21.7075 13.7075L14.7075 20.7075C14.6146 20.8005 14.5043 20.8742 14.3829 20.9246C14.2615 20.9749 14.1314 21.0008 14 21.0008C13.8686 21.0008 13.7385 20.9749 13.6171 20.9246C13.4957 20.8742 13.3854 20.8005 13.2925 20.7075L10.2925 17.7075C10.1049 17.5199 9.99945 17.2654 9.99945 17C9.99945 16.7346 10.1049 16.4801 10.2925 16.2925C10.4801 16.1049 10.7346 15.9994 11 15.9994C11.2654 15.9994 11.5199 16.1049 11.7075 16.2925L14 18.5863L20.2925 12.2925C20.3854 12.1996 20.4957 12.1259 20.6171 12.0756C20.7385 12.0253 20.8686 11.9994 21 11.9994C21.1314 11.9994 21.2615 12.0253 21.3829 12.0756C21.5043 12.1259 21.6146 12.1996 21.7075 12.2925C21.8004 12.3854 21.8741 12.4957 21.9244 12.6171C21.9747 12.7385 22.0006 12.8686 22.0006 13C22.0006 13.1314 21.9747 13.2615 21.9244 13.3829C21.8741 13.5043 21.8004 13.6146 21.7075 13.7075Z"
                fill="white"
              />
            </svg>
            <span className="change-phone-success-text">Number changed</span>
          </div>
        </div>
      )}
    </div>
  );
};

Profile.propTypes = {
  history: PropTypes.object,
  match: PropTypes.object,
};

export default Profile;
